import React, { useState, useMemo, useCallback } from 'react';
import { FaEdit, FaTrashAlt, FaUsers, FaSearch, FaFilter, FaGraduationCap, FaCalendarAlt, FaUserTie, FaChartLine, FaEllipsisV, FaSpinner, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import type { DiscipleshipClass } from '../../types/discipleship';

interface ClassListProps {
  classes: DiscipleshipClass[];
  onEdit: (cls: DiscipleshipClass) => void;
  onDelete: (id: string) => void;
  onManageStudents: (id: string) => void;
  isLoading?: boolean;
  studentCount?: number;
  completedStudentCount?: number;
  isClassCompleted?: boolean;
  classComputedStatus?: '진행중' | '완료됨' | '시작전' | '날짜미정'; // Status for the class
  error?: string | null;
}

export const ClassList: React.FC<ClassListProps> = React.memo(({
  classes,
  onEdit,
  onDelete,
  onManageStudents,
  isLoading = false,
  studentCount,
  completedStudentCount,
  isClassCompleted,
  classComputedStatus,
  error
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | '진행중' | '완료됨' | '시작전' | '날짜미정'>('all');
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);

  // 필터링된 클래스 - 성능 최적화
  const filteredClasses = useMemo(() => {
    return classes.filter(cls => {
      const matchesSearch =
        cls.programName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (cls.programNameOther && cls.programNameOther.toLowerCase().includes(searchTerm.toLowerCase())) ||
        cls.session.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cls.teacher.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = filterStatus === 'all' || classComputedStatus === filterStatus;
      
      return matchesSearch && matchesStatus;
    });
  }, [classes, searchTerm, filterStatus, classComputedStatus]);

  // 상태 배지 스타일 - 개선된 디자인
  const getStatusBadgeStyle = useCallback((status: string | undefined) => {
    switch (status) {
      case '진행중':
        return 'bg-gradient-to-r from-green-500 to-emerald-500 text-white dark:from-green-600 dark:to-emerald-600';
      case '완료됨':
        return 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white dark:from-blue-600 dark:to-cyan-600';
      case '시작전':
        return 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white dark:from-yellow-600 dark:to-orange-600';
      case '날짜미정':
        return 'bg-gradient-to-r from-gray-500 to-slate-500 text-white dark:from-gray-600 dark:to-slate-600';
      default:
        return 'bg-gradient-to-r from-gray-400 to-gray-500 text-white dark:from-gray-500 dark:to-gray-600';
    }
  }, []);

  // 상태별 아이콘 - 개선된 디자인
  const getStatusIcon = useCallback((status: string | undefined) => {
    switch (status) {
      case '진행중':
        return <FaChartLine className="h-3 w-3" />;
      case '완료됨':
        return <FaGraduationCap className="h-3 w-3" />;
      case '시작전':
        return <FaCalendarAlt className="h-3 w-3" />;
      default:
        return null;
    }
  }, []);

  // 날짜 포맷팅 함수
  const formatDate = useCallback((date: any) => {
    if (!date) return '-';
    try {
      return new Date(date.seconds * 1000).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).replace(/\.$/, '');
    } catch {
      return '-';
    }
  }, []);

  // 로딩 상태 처리 - 개선된 UI
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
        <div className="space-y-6">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                  <FaGraduationCap className="h-8 w-8 text-white" />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 오류 상태 처리
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
        <div className="flex items-center space-x-3">
          <FaExclamationTriangle className="h-6 w-6 text-red-500" />
          <div>
            <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">오류 발생</h3>
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // 빈 상태 처리 - 개선된 UI
  if (classes.length === 0) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl p-8 text-center border border-blue-200 dark:border-blue-800">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 mb-4">
          <FaGraduationCap className="h-10 w-10 text-white" />
        </div>
        <h3 className="mt-4 text-xl font-bold text-gray-800 dark:text-white">개설된 양육 과정이 없습니다</h3>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          '새 과정 개설' 버튼을 통해 새로운 양육 과정을 만들어보세요.
        </p>
        <div className="mt-6">
          <button className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-medium rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200">
            새 과정 개설
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
      {/* 헤더 영역 */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center">
              <FaGraduationCap className="mr-3" />
              양육 과정 관리
            </h2>
            <p className="text-blue-100 mt-1">교회 양육 과정을 효율적으로 관리하세요</p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-blue-100">
            <FaCheckCircle className="h-4 w-4" />
            <span>총 {classes.length}개 과정</span>
          </div>
        </div>
      </div>

      {/* 검색 및 필터 영역 - 개선된 UI */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          {/* 검색창 */}
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="과정 이름, 기수, 담당자로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
            />
          </div>
          
          {/* 상태 필터 */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="inline-flex items-center px-4 py-2.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 shadow-sm"
          >
            <option value="all">전체 상태</option>
            <option value="진행중">진행중</option>
            <option value="완료됨">완료됨</option>
            <option value="시작전">시작전</option>
            <option value="날짜미정">날짜미정</option>
          </select>
        </div>
        
        {/* 검색 결과 수 */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            총 <span className="font-semibold text-blue-600 dark:text-blue-400">{filteredClasses.length}</span>개의 양육 과정
            <span className="text-gray-400"> (전체 {classes.length}개)</span>
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>진행중</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>완료됨</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
              <span>시작전</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-5 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">과정 이름</th>
              <th scope="col" className="px-3 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">상태</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">기수</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">개설일</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">담당자</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">기간</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">등록인원</th>
              <th scope="col" className="px-4 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">수료인원</th>
              <th scope="col" className="px-5 py-4 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredClasses.map((cls) => (
              <tr
                key={cls.id}
                className={`hover:bg-gray-50 dark:hover:bg-gray-750 transition-all duration-200 ${selectedClassId === cls.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                role="row"
                onClick={() => setSelectedClassId(selectedClassId === cls.id ? null : cls.id)}
              >
                <td className="px-5 py-4 whitespace-nowrap text-sm font-medium text-center text-gray-900 dark:text-gray-200" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                      <FaGraduationCap className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-medium">{cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName}</span>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-center" role="cell">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${getStatusBadgeStyle(classComputedStatus)}`}>
                    {getStatusIcon(classComputedStatus)}
                    <span className="ml-2">{classComputedStatus || '-'}</span>
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400 font-medium" role="cell">
                  {cls.session}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">
                  {formatDate(cls.createdAt)}
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                      <FaUserTie className="h-3 w-3 text-white" />
                    </div>
                    <span className="font-medium">{cls.teacher}</span>
                  </div>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400" role="cell">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm">
                    {cls.durationInWeeks}주
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-medium" role="cell">
                  <span className="text-green-600 dark:text-green-400 font-bold">
                    {studentCount !== undefined ? `${studentCount}명` : '로딩...'}
                  </span>
                </td>
                <td className="px-4 py-4 whitespace-nowrap text-sm text-center font-medium" role="cell">
                  <span className={isClassCompleted && completedStudentCount !== undefined ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-400 dark:text-gray-500'}>
                    {isClassCompleted && completedStudentCount !== undefined ? `${completedStudentCount}명` : (isClassCompleted ? '로딩...' : '-')}
                  </span>
                </td>
                <td className="px-5 py-4 whitespace-nowrap text-center text-sm" role="cell">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onManageStudents(cls.id);
                      }}
                      className="p-2 text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                      aria-label={`Manage students for ${cls.programName}`}
                    >
                      <FaUsers size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(cls);
                      }}
                      className="p-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      aria-label={`Edit ${cls.programName}`}
                    >
                      <FaEdit size={14} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(cls.id);
                      }}
                      className="p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label={`Delete ${cls.programName}`}
                    >
                      <FaTrashAlt size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {filteredClasses.map((cls) => (
          <div
            key={cls.id}
            className={`p-4 bg-white dark:bg-gray-800 transition-all duration-200 ${selectedClassId === cls.id ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setSelectedClassId(selectedClassId === cls.id ? null : cls.id)}
          >
            {/* 헤더 영역 */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white shadow-lg">
                  <FaGraduationCap />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                    {cls.programName === 'other' && cls.programNameOther ? cls.programNameOther : cls.programName}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{cls.session}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm ${getStatusBadgeStyle(classComputedStatus)}`}>
                  {getStatusIcon(classComputedStatus)}
                  <span className="ml-2">{classComputedStatus || '-'}</span>
                </span>
                <button className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <FaEllipsisV size={16} />
                </button>
              </div>
            </div>
            
            {/* 정보 영역 */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <FaUserTie className="h-3 w-3 text-white" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">담당자:</span> {cls.teacher}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 bg-gradient-to-br from-gray-500 to-slate-500 rounded-full flex items-center justify-center">
                  <FaCalendarAlt className="h-3 w-3 text-white" />
                </div>
                <span className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium">개설일:</span> {formatDate(cls.createdAt)}
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-sm">
                  <span className="font-medium">기간:</span> {cls.durationInWeeks}주
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-green-600 dark:text-green-400 font-bold">
                  <span className="text-gray-700 dark:text-gray-300">등록:</span> {studentCount !== undefined ? `${studentCount}명` : '로딩...'}
                </span>
              </div>
              
              <div className="flex items-center space-x-2 col-span-2">
                <span className={isClassCompleted && completedStudentCount !== undefined ? 'text-blue-600 dark:text-blue-400 font-bold' : 'text-gray-400 dark:text-gray-500'}>
                  <span className="text-gray-700 dark:text-gray-300 font-medium">수료:</span> {isClassCompleted && completedStudentCount !== undefined ? `${completedStudentCount}명` : (isClassCompleted ? '로딩...' : '-')}
                </span>
              </div>
            </div>
            
            {/* 관리 버튼 */}
            <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onManageStudents(cls.id);
                }}
                className="px-3 py-1.5 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200 flex items-center space-x-1"
              >
                <FaUsers size={12} />
                <span>학생 관리</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(cls);
                }}
                className="px-3 py-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-all duration-200 flex items-center space-x-1"
              >
                <FaEdit size={12} />
                <span>수정</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(cls.id);
                }}
                className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200 flex items-center space-x-1"
              >
                <FaTrashAlt size={12} />
                <span>삭제</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

ClassList.displayName = 'ClassList';
